<template>
  <!--
    เมนูกระบวนงานของ Admin (รูปแบบกลาง) — ข้อมูลเมนูอยู่ที่ adminProcessMenus.js
    stages[]: { key, no?, icon?, title, desc, aside?, items: [{ text, icon, to }] }
      no     มีเลข = ขั้นตอนตามลำดับ (มีลูกศรเชื่อมขั้นถัดไป) · ไม่มีเลข = กลุ่มข้อมูล (ใช้ icon)
      aside  คอลัมน์เครื่องมือท้ายสุด (เส้นประ)
    ไฮไลต์ขั้น/หน้าที่เปิดอยู่ · ย่อเหลือแถวเดียวได้ (จำค่าในเครื่อง ใช้ร่วมทุกเมนู)
  -->
  <div class="atn" :class="{ 'atn--collapsed': collapsed }">
    <div class="atn-head">
      <v-icon small class="mr-2" color="#1f3b57">{{ icon }}</v-icon>
      <span class="atn-title">{{ title }}</span>
      <span v-if="current" class="atn-crumb">
        <v-icon x-small class="mx-1">mdi-chevron-right</v-icon>
        <template v-if="currentStage.no">ขั้น {{ currentStage.no }} · </template>{{ currentStage.title }}
        <v-icon x-small class="mx-1">mdi-chevron-right</v-icon>
        <b>{{ current.text }}</b>
      </span>
      <v-spacer />
      <v-btn small outlined color="#1f3b57" class="atn-toggle" @click="toggle">
        <v-icon small left>{{ collapsed ? "mdi-chevron-down" : "mdi-chevron-up" }}</v-icon>
        {{ collapsed ? "แสดงเมนูทั้งหมด" : "ย่อเมนู" }}
      </v-btn>
    </div>

    <!-- ย่อ: แถวเดียว กดเพื่อไปหน้าแรกของขั้น/กลุ่ม -->
    <div v-if="collapsed" class="atn-strip">
      <template v-for="(s, i) in stages">
        <router-link :key="s.key" :to="s.items[0].to" class="atn-chip"
          :class="{ active: s.key === currentStage.key, aside: s.aside, plain: !s.no }">
          <span v-if="s.no" class="atn-num">{{ s.no }}</span>
          <v-icon v-else x-small class="mr-1">{{ s.icon }}</v-icon>
          {{ s.title }}
        </router-link>
        <v-icon v-if="hasArrow(i)" :key="s.key + '-arrow'" x-small color="#9aa5b1">mdi-chevron-right</v-icon>
      </template>
    </div>

    <!-- เต็ม: คอลัมน์ละขั้น/กลุ่ม -->
    <div v-else class="atn-grid" :style="{ '--atn-cols': gridColumns }">
      <section v-for="(s, i) in stages" :key="s.key" class="atn-stage"
        :class="{ active: s.key === currentStage.key, aside: s.aside, arrow: hasArrow(i) }">
        <header class="atn-stage-head">
          <span v-if="s.no" class="atn-num">{{ s.no }}</span>
          <v-icon v-else small class="atn-group-icon">{{ s.icon }}</v-icon>
          <div>
            <div class="atn-stage-title">{{ s.title }}</div>
            <div v-if="s.desc" class="atn-stage-desc">{{ s.desc }}</div>
          </div>
        </header>
        <router-link v-for="it in s.items" :key="it.to" :to="it.to" class="atn-link"
          :class="{ active: current && current.to === it.to }">
          <v-icon small class="atn-link-icon">{{ it.icon }}</v-icon>
          <span>{{ it.text }}</span>
        </router-link>
      </section>
    </div>
  </div>
</template>

<script>
const STORAGE_KEY = "admin_process_nav_collapsed";

/** เส้นทางเทียบแบบไม่สนใจ "/" ท้าย — เมนูที่มี query (เช่น ?type=C) เทียบทั้ง path+query */
function norm(p) {
  const [path, q] = String(p || "").split("?");
  const clean = path.replace(/\/+$/, "") || "/";
  return q ? `${clean}?${q}` : clean;
}

export default {
  name: "AdminProcessNav",
  props: {
    title: { type: String, required: true },
    icon: { type: String, default: "mdi-sitemap" },
    stages: { type: Array, required: true }
  },
  data() {
    let collapsed = false;
    try {
      collapsed = localStorage.getItem(STORAGE_KEY) === "1";
    } catch (e) {
      // ไม่มี localStorage — ใช้ค่าเริ่มต้น
    }
    return { collapsed };
  },
  computed: {
    found() {
      if (!this.$route) return null;
      const full = norm(this.$route.fullPath);
      const path = norm(this.$route.path);
      let fallback = null;
      for (const s of this.stages) {
        for (const it of s.items) {
          const to = norm(it.to);
          if (to === full) return { stage: s, item: it };
          if (!fallback && !to.includes("?") && to === path) fallback = { stage: s, item: it };
        }
      }
      return fallback;
    },
    current() {
      return this.found ? this.found.item : null;
    },
    currentStage() {
      return this.found ? this.found.stage : {};
    },
    // คอลัมน์ที่มีเมนูมากให้กว้างขึ้นเล็กน้อย
    gridColumns() {
      return this.stages.map(s => (s.items.length >= 5 ? "1.35fr" : "1fr")).join(" ");
    }
  },
  methods: {
    hasArrow(i) {
      const a = this.stages[i];
      const b = this.stages[i + 1];
      return !!(a && b && a.no && b.no);
    },
    toggle() {
      this.collapsed = !this.collapsed;
      try {
        localStorage.setItem(STORAGE_KEY, this.collapsed ? "1" : "0");
      } catch (e) {
        // ไม่บันทึกก็ได้
      }
    }
  }
};
</script>

<style scoped>
.atn {
  --atn-ink: #1f3b57;
  --atn-muted: #6b7785;
  --atn-line: #e3e8ee;
  --atn-accent: #c62828;
  --atn-accent-soft: #fdecec;
  background: #fff;
  border: 1px solid var(--atn-line);
  border-radius: 10px;
  padding: 10px 12px 12px;
  margin-bottom: 16px;
}
.atn-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  margin-bottom: 10px;
}
.atn-title {
  font-weight: 700;
  color: var(--atn-ink);
  font-size: 0.95rem;
}
.atn-crumb {
  color: var(--atn-muted);
  font-size: 0.82rem;
}
.atn-crumb b {
  color: var(--atn-accent);
}
.atn-toggle {
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.8rem;
}
.atn--collapsed .atn-head {
  margin-bottom: 8px;
}

/* ---------- เต็ม ---------- */
.atn-grid {
  display: grid;
  grid-template-columns: var(--atn-cols);
  gap: 10px;
}
.atn-stage {
  border: 1px solid var(--atn-line);
  border-top: 3px solid #b8c4d0;
  border-radius: 8px;
  padding: 8px 8px 6px;
  background: #fafbfc;
  position: relative;
  min-width: 0;
}
.atn-stage.active {
  border-top-color: var(--atn-accent);
  background: #fff;
  box-shadow: 0 2px 8px rgba(31, 59, 87, 0.08);
}
.atn-stage.aside {
  background: #f4f6f9;
  border-style: dashed;
  border-top-style: solid;
}
.atn-stage.arrow::after {
  /* ลูกศรไปขั้นถัดไป */
  content: "";
  position: absolute;
  right: -9px;
  top: 18px;
  width: 8px;
  height: 8px;
  border-top: 2px solid #b8c4d0;
  border-right: 2px solid #b8c4d0;
  transform: rotate(45deg);
}
.atn-stage-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
  padding: 0 2px;
}
.atn-num {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--atn-ink);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.atn-stage.active .atn-num,
.atn-chip.active .atn-num {
  background: var(--atn-accent);
}
.atn-group-icon {
  color: var(--atn-ink) !important;
  margin-top: 2px;
}
.atn-stage.active .atn-group-icon {
  color: var(--atn-accent) !important;
}
.atn-stage-title {
  font-weight: 700;
  color: var(--atn-ink);
  font-size: 0.86rem;
  line-height: 1.25;
}
.atn-stage-desc {
  color: var(--atn-muted);
  font-size: 0.72rem;
  line-height: 1.3;
}
.atn-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 6px;
  color: #2f3d4a !important;
  text-decoration: none;
  font-size: 0.8rem;
  line-height: 1.3;
  transition: background 0.15s;
}
.atn-link:hover {
  background: #eef2f6;
}
.atn-link-icon {
  color: #6b7785 !important;
}
.atn-link.active {
  background: var(--atn-accent-soft);
  color: var(--atn-accent) !important;
  font-weight: 700;
}
.atn-link.active .atn-link-icon {
  color: var(--atn-accent) !important;
}

/* ---------- ย่อ ---------- */
.atn-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.atn-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 4px;
  border: 1px solid var(--atn-line);
  border-radius: 999px;
  color: var(--atn-ink) !important;
  text-decoration: none;
  font-size: 0.8rem;
  background: #fafbfc;
}
.atn-chip.plain {
  padding-left: 10px;
}
.atn-chip.aside {
  margin-left: auto;
  border-style: dashed;
}
.atn-chip.active {
  border-color: var(--atn-accent);
  background: var(--atn-accent-soft);
  color: var(--atn-accent) !important;
  font-weight: 700;
}

@media (max-width: 1263px) {
  .atn-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .atn-stage.arrow::after {
    display: none;
  }
}
@media (max-width: 700px) {
  .atn-grid {
    grid-template-columns: 1fr;
  }
  .atn-chip.aside {
    margin-left: 0;
  }
}
</style>
